import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { clubLabel } from "./Utils";

type TreeNode = {
  _id: string;
  memberId: string;
  name: string;
  club?: number;
  buyerId?: string | null;
  rank?: "Leader" | "Director" | "Partner" | "Royalty" | null;
  match?: boolean;
  isActive?: boolean;
  position?: number | null;
  children?: Array<TreeNode | null>;
};

type TreeProps = {
  node: TreeNode;
  linkToProfile?: boolean;
  compact?: boolean;
  defaultExpandDepth?: number;
  filtered?: boolean;
};

const LEG_LABELS = ["Leg 1", "Leg 2", "Leg 3"];

const padChildren = (node: TreeNode): Array<TreeNode | null> => {
  const children = Array.isArray(node.children) ? [...node.children.slice(0, 3)] : [];
  while (children.length < 3) children.push(null);
  return children;
};

const nodeKey = (node: TreeNode) => node._id || node.memberId;

const collectExpanded = (
  node: TreeNode | null,
  depth: number,
  maxDepth: number,
  acc: Set<string>,
) => {
  if (!node) return;
  if (depth < maxDepth) {
    acc.add(nodeKey(node));
    padChildren(node).forEach((child) =>
      collectExpanded(child, depth + 1, maxDepth, acc),
    );
  }
};

const collectAllExpandable = (node: TreeNode | null, acc: Set<string>) => {
  if (!node) return;
  acc.add(nodeKey(node));
  padChildren(node).forEach((child) => collectAllExpandable(child, acc));
};

const TreeCard = ({
  node,
  isRoot,
  expanded,
  linkToProfile,
  onToggle,
}: {
  node: TreeNode;
  isRoot?: boolean;
  expanded: boolean;
  linkToProfile?: boolean;
  onToggle: () => void;
}) => {
  const inactive = node.isActive === false;
  const memberIdLabel = node.memberId || "—";
  const memberId =
    linkToProfile && node._id ? (
      <Link
        to={`/sale-users/${node._id}`}
        className="mlm-card-id"
        onClick={(event) => event.stopPropagation()}
      >
        {memberIdLabel}
      </Link>
    ) : (
      <strong className="mlm-card-id">{memberIdLabel}</strong>
    );

  return (
    <div className={`mlm-card ${isRoot ? "is-root" : ""} ${inactive ? "is-inactive" : "is-active"}`}>
      <div className="mlm-card-body">
        {memberId}
        <span className="mlm-card-name" title={node.name}>
          {node.name || "Member"}
        </span>
        {node.buyerId && <span className="mlm-card-name">{node.buyerId}</span>}
        {node.club != null && <span className="mlm-card-name">{clubLabel(node.club)}</span>}
        {node.rank && <span className="mlm-card-name">Rank: {node.rank}</span>}
        {node.match === false && <span className="mlm-card-name">Placement ancestor</span>}
        {/* <span className="mlm-card-fill">{filtered ? `${filled} visible legs` : `${filled}/3`}</span> */}
      </div>
      <button
        type="button"
        className={`mlm-card-toggle ${expanded ? "is-open" : ""}`}
        aria-label={expanded ? "Collapse downline" : "Expand downline"}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onToggle();
        }}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    </div>
  );
};

const EmptySlot = ({ position, filtered }: { position: number; filtered?: boolean }) => (
  <div className="mlm-card is-empty">
    <div className="mlm-card-body">
      <strong>{filtered ? "No match" : "Open"}</strong>
      <span className="mlm-card-name">{LEG_LABELS[position - 1]}</span>
    </div>
  </div>
);

const TreeItem = ({
  node,
  isRoot,
  expanded,
  linkToProfile,
  onToggle,
  filtered,
}: {
  node: TreeNode;
  filtered?: boolean;
  isRoot?: boolean;
  expanded: Set<string>;
  linkToProfile?: boolean;
  onToggle: (id: string) => void;
}) => {
  const children = padChildren(node);
  const key = nodeKey(node);
  const isOpen = expanded.has(key);

  return (
    <li className={isRoot ? "is-root" : ""}>
      <TreeCard
        node={node}
        isRoot={isRoot}
        expanded={isOpen}
        linkToProfile={linkToProfile}
        onToggle={() => onToggle(key)}
      />
      {isOpen && (
        <ul>
          {children.map((child, index) =>
            child ? (
              <TreeItem
                key={nodeKey(child)}
                node={child}
                expanded={expanded}
                linkToProfile={linkToProfile}
                onToggle={onToggle}
                filtered={filtered}
              />
            ) : (
              <li key={`empty-${key}-${index}`}>
                <EmptySlot position={index + 1} filtered={filtered} />
              </li>
            ),
          )}
        </ul>
      )}
    </li>
  );
};

const NetworkTree = ({
  node,
  linkToProfile = true,
  compact = false,
  defaultExpandDepth = 1,
  filtered = false,
}: TreeProps) => {
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const next = new Set<string>();
    collectExpanded(node, 0, defaultExpandDepth, next);
    return next;
  });
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const next = new Set<string>();
    collectExpanded(node, 0, defaultExpandDepth, next);
    setExpanded(next);
    setScale(1);
  }, [node, defaultExpandDepth]);

  const allIds = useMemo(() => {
    const next = new Set<string>();
    collectAllExpandable(node, next);
    return next;
  }, [node]);

  if (!node) return <div className="empty-state">No tree data.</div>;

  const toggle = (id: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className={`mlm-tree-wrap ${compact ? "is-compact" : ""}`}>
      <div className="mlm-tree-toolbar">
        <div className="mlm-tree-legend">
          <span>
            <i className="mlm-dot is-active" />
            Active
          </span>
          <span>
            <i className="mlm-dot is-inactive" />
            Inactive
          </span>
          <span>
            <i className="mlm-dot is-open" />
            Open slot
          </span>
        </div>
        <div className="mlm-tree-actions">
          <button
            type="button"
            className="btn-sm btn-outline"
            onClick={() => setExpanded(new Set(allIds))}
          >
            Expand all
          </button>
          <button
            type="button"
            className="btn-sm btn-outline"
            onClick={() => {
              const next = new Set<string>();
              collectExpanded(node, 0, 1, next);
              setExpanded(next);
            }}
          >
            Collapse
          </button>
          <div className="mlm-zoom">
            <button
              type="button"
              aria-label="Zoom out"
              onClick={() => setScale((value) => Math.max(0.6, Number((value - 0.1).toFixed(1))))}
            >
              −
            </button>
            <span>{Math.round(scale * 100)}%</span>
            <button
              type="button"
              aria-label="Zoom in"
              onClick={() => setScale((value) => Math.min(1.4, Number((value + 0.1).toFixed(1))))}
            >
              +
            </button>
          </div>
        </div>
      </div>

      <div className="mlm-tree-scroll">
        <div className="mlm-tree" style={{ zoom: scale }}>
          <ul>
            <TreeItem
              node={node}
              isRoot
              expanded={expanded}
              linkToProfile={linkToProfile}
              onToggle={toggle}
              filtered={filtered}
            />
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NetworkTree;
